// ---------------------------------------------------------------------------
// Casca de desktop — a decisão, não o desenho.
//
// Este arquivo é NÚCLEO: vai para todo bundle, inclusive o nativo, então fica
// pequeno de propósito. O desenho (barra lateral + topo) mora em
// `src/desktop/`, que só existe na build web e só é BAIXADO quando esta decisão
// diz sim — ver `AppFrame.tsx` e o bloco "desktop" do `mobile-postbuild.mjs`.
//
// A regra (mock `redesign-livra/web-desktop/`, aprovada em 30/09/2026):
//  · só na build WEB — no app das lojas o código nem existe;
//  · só com tela ≥ 1024px — abaixo disso é o frame de 430px de sempre
//    (768–1023) ou o celular;
//  · só com sessão de verdade — visitante anônimo (perfil público) não tem
//    para onde ir numa barra de navegação de app;
//  · nunca no `/onboarding` — lá a decisão foi "cartão central", e o wizard
//    é a única rota emoldurada sem BottomNav.
//
// Quem decide é a LARGURA medida no cliente, não a user-agent: janela
// redimensionada e iPad com teclado quebrariam a outra regra.
//
// Ligada para TODOS desde 30/09 (noite), com as telas das duas levas do mock
// no desenho de desktop. Entre a fatia 1 e isto ela ficou atrás de um opt-in
// (`?desktop=1`): a casca subiu antes das páginas, e toda tela na coluna de
// 430px numa janela de 1900px ficou pior que o frame antigo (fundador: "não tá
// condizente com o mock"). O que sobra daquela fase é a SAÍDA por aparelho:
// `?desktop=0` volta à coluna de 430px e fica gravado; `?desktop=1` desfaz.
// ---------------------------------------------------------------------------

import {
  createContext,
  useContext,
  useLayoutEffect,
  useState,
  useSyncExternalStore,
  type CSSProperties,
} from "react";
import { APP_FRAME_ID } from "./overlayHost";

/** Corte do desktop. Espelha o `lg` do Tailwind (64rem = 1024px). */
export const DESKTOP_SHELL_QUERY = "(min-width: 1024px)";

/** `true` na build do app das lojas (`build:mobile`). Constante de build. */
export const IS_NATIVE_BUILD = import.meta.env.VITE_NATIVE_BUILD === "true";

/** Chave da SAÍDA por aparelho no `localStorage` (`?desktop=0`). */
export const DESKTOP_SHELL_OPT_OUT_KEY = "livra:desktop-shell-off";

/** Chave do opt-in da fase de testes (30/09) — hoje só é apagada. */
const LEGACY_OPT_IN_KEY = "livra:desktop-shell";

/**
 * A casca vale neste aparelho? Sim, salvo quem saiu: `?desktop=0` desliga e
 * grava, `?desktop=1` religa. Armazenamento bloqueado = o padrão (ligada) —
 * a saída é conveniência, não a regra.
 */
export function readDesktopShellEnabled(search: string = window.location.search): boolean {
  try {
    const q = new URLSearchParams(search).get("desktop");
    window.localStorage.removeItem(LEGACY_OPT_IN_KEY);
    if (q === "0") window.localStorage.setItem(DESKTOP_SHELL_OPT_OUT_KEY, "1");
    if (q === "1") window.localStorage.removeItem(DESKTOP_SHELL_OPT_OUT_KEY);
    return window.localStorage.getItem(DESKTOP_SHELL_OPT_OUT_KEY) !== "1";
  } catch {
    return true;
  }
}

/**
 * Dica "a última sessão neste aparelho era de alguém logado" (05/10, medição
 * do "desktop abre como celular"). O 1º `onAuthStateChanged` leva de 50 ms a
 * 1,5 s; sem a dica, `hasRealSession` é falso nesse intervalo e a casca nasce
 * desligada — o boot aparece na coluna de 430px e pula para o lado da barra
 * lateral quando o Auth responde. Com a dica, a casca já nasce ligada.
 *
 * Quem grava e apaga é o `AppFrame`, sempre que o Auth assenta: sessão real
 * grava, sem sessão (logout, token morto) ou anônima apaga. Errar a dica custa
 * um quadro de casca antes do `/login` — nunca acesso, que é dos guards.
 */
export const REAL_SESSION_HINT_KEY = "livra:desktop-real-session";

export function readRealSessionHint(): boolean {
  try {
    return window.localStorage.getItem(REAL_SESSION_HINT_KEY) === "1";
  } catch {
    return false;
  }
}

export function writeRealSessionHint(real: boolean): void {
  try {
    if (real) window.localStorage.setItem(REAL_SESSION_HINT_KEY, "1");
    else window.localStorage.removeItem(REAL_SESSION_HINT_KEY);
  } catch {
    // armazenamento bloqueado: sem dica, a casca só espera o Auth (o de antes)
  }
}

/**
 * Sessão para a decisão da casca: a real, ou — SÓ enquanto o Auth não
 * respondeu — a dica da última sessão.
 */
export function shellSessionGuess({
  hasUser,
  isAnonymous,
  isAuthLoading,
  hint,
}: {
  hasUser: boolean;
  isAnonymous: boolean;
  isAuthLoading: boolean;
  hint: boolean;
}): boolean {
  if (hasUser) return !isAnonymous;
  return isAuthLoading && hint;
}

export interface DesktopShellInput {
  /** O aparelho não saiu da casca (`?desktop=0`). */
  isEnabledOnDevice: boolean;
  isNativeBuild: boolean;
  isWideViewport: boolean;
  hasRealSession: boolean;
  pathname: string;
}

/** A regra inteira, pura — é ela que os testes cobram. */
export function shouldUseDesktopShell({
  isEnabledOnDevice,
  isNativeBuild,
  isWideViewport,
  hasRealSession,
  pathname,
}: DesktopShellInput): boolean {
  if (!isEnabledOnDevice || isNativeBuild || !isWideViewport || !hasRealSession) return false;
  return !(pathname === "/onboarding" || pathname.startsWith("/onboarding/"));
}

/**
 * Rotas que o VISITANTE sem conta abre pelo link (bio, story, DM): perfil
 * público, estante pública e "lendo agora". Mock
 * `redesign-livra/web-desktop/web-desktop-visitante.html` (V4, aprovado 30/09).
 */
const VISITOR_ROUTES: RegExp[] = [
  /^\/profile\/[^/]+\/?$/,
  /^\/profile\/[^/]+\/shelf\/[^/]+\/?$/,
  /^\/lendo\/[^/]+\/?$/,
];

export interface VisitorShellInput {
  isEnabledOnDevice: boolean;
  isNativeBuild: boolean;
  isWideViewport: boolean;
  /**
   * A sessão ANÔNIMA já existe. É ela, e não "não há `user`", que liga: o
   * `AnonymousOrGuarded` só cria a anônima depois de o Auth decidir — então
   * quem tem conta nunca vê o topo de visitante piscar enquanto o Auth carrega.
   */
  hasAnonymousSession: boolean;
  pathname: string;
}

/**
 * Casca do VISITANTE: topo com a marca e "Entrar / Criar conta grátis", frame
 * largo, sem barra lateral nem barra de baixo — só nas três rotas acima. Fora
 * delas o visitante segue como hoje (login, landing, ficha pública do livro).
 */
export function shouldUseVisitorShell({
  isEnabledOnDevice,
  isNativeBuild,
  isWideViewport,
  hasAnonymousSession,
  pathname,
}: VisitorShellInput): boolean {
  if (!isEnabledOnDevice || isNativeBuild || !isWideViewport || !hasAnonymousSession) return false;
  return VISITOR_ROUTES.some((r) => r.test(pathname));
}

function subscribeWide(onChange: () => void): () => void {
  if (typeof window === "undefined" || !window.matchMedia) return () => {};
  const mql = window.matchMedia(DESKTOP_SHELL_QUERY);
  mql.addEventListener("change", onChange);
  return () => mql.removeEventListener("change", onChange);
}

function readWide(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia(DESKTOP_SHELL_QUERY).matches;
}

/**
 * A tela tem largura de desktop? Síncrono já no primeiro render
 * (`useSyncExternalStore` lê o `matchMedia` na hora), então a casca certa
 * nasce na primeira pintura, sem piscar o layout de celular antes.
 */
export function useWideViewport(): boolean {
  return useSyncExternalStore(subscribeWide, readWide, () => false);
}

export interface DesktopShellValue {
  /** Onde o Modo Foco minimizado se desenha (cartão na barra lateral). */
  focusSlot: HTMLElement | null;
  setFocusSlot: (el: HTMLElement | null) => void;
  /**
   * A página aberta tem desenho de desktop próprio e quer a área INTEIRA ao
   * lado da barra lateral. Sem isso, o frame é a coluna de 430px de sempre.
   * Quem liga é a página, por `useWideDesktopFrame()`.
   */
  wideFrame: boolean;
  /**
   * CONTADO, não booleano: `true` soma um pedido, `false` devolve um. Página e
   * componente de dentro (ex.: a aba da Liga) podem pedir juntos, e a aba que
   * desmonta ao trocar não pode devolver a coluna de 430px para a página que
   * continua na tela.
   */
  setWideFrame: (wide: boolean) => void;
  /**
   * Modo Foco em tela cheia (mock, seção 27): a cena cobre a JANELA, por cima
   * da barra lateral e do topo. O frame sobe acima da casca enquanto isso —
   * é ele o containing block do Foco e das folhas que o Foco abre.
   */
  setFrameRaised?: (raised: boolean) => void;
  /** `"visitor"` = casca de quem abriu um link sem conta (ver `shouldUseVisitorShell`). */
  kind?: "app" | "visitor";
  /** Tema da DONA da página visitada, para o topo e o rodapé do visitante. */
  ownerTheme?: OwnerTheme | null;
  setOwnerTheme?: (theme: OwnerTheme | null) => void;
}

/** O tema que a página visitada veste — o mesmo par `data-theme` + estilo do wrapper dela. */
export interface OwnerTheme {
  themeId?: string;
  style?: CSSProperties;
}

/**
 * Presente só dentro da casca de desktop. `null` = celular, tablet, app
 * nativo, onboarding ou visitante — todo o comportamento de sempre.
 */
export const DesktopShellContext = createContext<DesktopShellValue | null>(null);

export function useDesktopShell(): DesktopShellValue | null {
  return useContext(DesktopShellContext);
}

/**
 * Chamado pelo layout de desktop de uma página (`src/desktop/pages/*`): liga o
 * frame largo enquanto ele está montado e devolve a coluna ao desmontar — a
 * próxima rota sem desenho próprio volta para os 430px sem saber de nada.
 * `useLayoutEffect` para a largura mudar antes da primeira pintura da página.
 */
export function useWideDesktopFrame(): void {
  const setWide = useDesktopShell()?.setWideFrame;
  useLayoutEffect(() => {
    if (!setWide) return;
    setWide(true);
    return () => setWide(false);
  }, [setWide]);
}

/**
 * Página (ou peça) com desenho de desktop: liga o frame largo enquanto montada
 * e diz se a casca está ativa. Atalho de `useDesktopShell() !== null` +
 * `useWideDesktopFrame()`, o par que toda tela da casca repete.
 *
 * ⚠️ Fora de `src/desktop/` o desenho largo é CLASSE CONDICIONAL, nunca
 * variante responsiva (`lg:`/`xl:`) — a guarda `frameResponsiveVariants`
 * explica por quê. Arranjo que precise de corte por largura (duas colunas só a
 * partir de 1280px) usa o `SplitDesktopLayout` (`lib/layout/desktopPages.ts`).
 */
export function useDesktopWide(): boolean {
  const wide = useDesktopShell() !== null;
  useWideDesktopFrame();
  return wide;
}

/**
 * Cobre a JANELA inteira a partir de dentro do frame — o Modo Foco em tela
 * cheia no desktop (mock, seção 27: a cena passa por cima da barra lateral e
 * do topo, como no celular).
 *
 * Por que não um portal para o `body`: o Foco abre folhas próprias (anotar a
 * sessão, o Studio), e elas saem por portal para o `#app-overlay-host`, DENTRO
 * do frame. Com o Foco fora, o frame (transformado, contexto de empilhamento
 * próprio) ficaria atrás dele e as folhas nasceriam invisíveis — a classe do
 * BUG-127. Aqui o Foco continua no frame e só se estica: o frame é o containing
 * block do `fixed`, então offsets negativos iguais à posição dele levam a caixa
 * à borda da janela; e o frame sobe acima da casca (`setFrameRaised`) enquanto
 * isso durar.
 *
 * Mede a posição do frame em vez de somar as variáveis da casca: o frame é a
 * coluna de 430px OU o largo, e a conta seria diferente em cada um.
 *
 * `undefined` fora da casca (celular, app das lojas): lá o `inset-0` de sempre
 * já é a janela.
 */
export function useWindowCoverStyle(enabled: boolean): CSSProperties | undefined {
  const shell = useDesktopShell();
  const on = enabled && shell !== null;
  const raise = shell?.setFrameRaised;
  const [box, setBox] = useState<{ x: number; y: number; w: number; h: number } | null>(null);

  useLayoutEffect(() => {
    // Desligado não zera a medida: o retorno abaixo já ignora `box` quando
    // `on` é falso, e a próxima abertura mede de novo antes de pintar.
    if (!on) return;
    raise?.(true);
    const measure = () => {
      const r = document.getElementById(APP_FRAME_ID)?.getBoundingClientRect();
      setBox({ x: r?.left ?? 0, y: r?.top ?? 0, w: window.innerWidth, h: window.innerHeight });
    };
    measure();
    window.addEventListener("resize", measure);
    return () => {
      window.removeEventListener("resize", measure);
      raise?.(false);
    };
  }, [on, raise]);

  if (!on || !box) return undefined;
  return { left: -box.x, top: -box.y, right: "auto", bottom: "auto", width: box.w, height: box.h };
}

/**
 * A página visitada publica o tema da dona para a casca do visitante: o topo e
 * o rodapé ficam FORA do wrapper `data-theme` da página e, sem isto, sairiam no
 * tema do visitante — uma faixa de outra cor em cima de um perfil escuro (a
 * mesma lição do BUG-80: quem visita vê o que a dona vê). No-op fora da casca.
 */
export function useVisitorOwnerTheme(themeId: string | undefined, style: CSSProperties | undefined): void {
  const set = useDesktopShell()?.setOwnerTheme;
  useLayoutEffect(() => {
    if (!set) return;
    set({ themeId, style });
    return () => set(null);
  }, [set, themeId, style]);
}
